<template>
    <div class="EntityDetailsAddressForm">
        <FormElement :label="form.schema.title">
            <JsonSchemaAddressForm
                :id="id"
                :form-element-component="addressFormElementComponent"
                :depth="depth + 1"
                :form="form"
                :disabled="disabled || form.schema.readOnly"
                :custom-component-input="customComponentInput"
                :value="localValue"
                :touched="touched"
                :strings="strings"
                :rbac-disabled="rbacDisabled"
                :wrap-value-mode="wrapValueMode"
                :required="required"
                :deletable="!required"
                v-bind="$attrs"
                @input="onInput"
                @change="onChange"
            />
        </FormElement>
    </div>
</template>

<script lang="ts">
    import { markRaw } from 'vue';

    import { Component, Watch } from '@evidentid/vue-property-decorator';

    import { FormElement } from '@evidentid/dashboard-commons/components/Form';
    import {
        JsonSchemaFormElement,
    } from '@evidentid/dashboard-commons/components/JsonSchemaForm';
    import JsonSchemaAddressForm
        from '@evidentid/dashboard-commons/components/JsonSchemaForm/customElements/JsonSchemaAddressForm.vue';
    import AbstractJsonSchemaForm
        from '@evidentid/dashboard-commons/components/JsonSchemaForm/elements/AbstractJsonSchemaForm';
    import { JsonFormObject } from '@evidentid/json-schema/interfaces/JsonForm';
    import { EntityAddress } from '@evidentid/tprm-portal-lib/models/dashboard';

    @Component({
        emits: [ 'change', 'input' ],
        components: {
            FormElement,
            JsonSchemaAddressForm,
            JsonSchemaFormElement,
        },
        inheritAttrs: false,
    })
    export default class EntityDetailsAddressForm
        extends AbstractJsonSchemaForm<JsonFormObject, EntityDetailsAddressForm | undefined> {
        // PRODUCT-26110 Phase 2B2: guard the field initializer. During the edit->view
        // remount that Cancel triggers, this component is briefly constructed while the
        // `form` prop is still undefined (the parent's FormController is being rebuilt),
        // and `this.form.getValue(...)` crashed the whole page to the error screen. The
        // immediate `@Watch('value')` below re-derives localValue as soon as real props
        // arrive.
        private localValue = this.form ? this.form.getValue(this.value, true) : null;
        // Named apart from the inherited `formElementComponent` prop on
        // AbstractJsonSchemaForm, which it deliberately ignores: the inner address form
        // always uses the dashboard-commons dispatcher, whatever this component received.
        // markRaw because a class field becomes reactive data, and Vue warns when it has
        // to resolve a component definition that has been wrapped in a proxy.
        private addressFormElementComponent = markRaw(JsonSchemaFormElement);

        @Watch('form')
        @Watch('value', { immediate: true })
        private onValueChanges(): void {
            // PRODUCT-26110 Phase 2B2: same transient-undefined guard as the field
            // initializer above (this immediate watcher also runs during construction).
            if (!this.form) {
                return;
            }
            // When the address has been deleted (value is null/undefined), preserve null
            // rather than calling form.getValue which reconstructs an empty address object.
            // An empty address object fails schema validation and blocks saving.
            this.localValue = this.value == null ? null : this.form.getValue(this.value, true);
        }

        private onInput(val: EntityAddress) {
            this.localValue = val;
            this.$emit('input', val);
            // Some sub-fields (e.g. country dropdown) only fire @input, never @change.
            // Emit change here so the parent dirty-tracking chain fires on every field update.
            this.$emit('change', val); // fix: ensure dirty state propagates
        }

        private onChange(val: EntityAddress) {
            this.localValue = val;
            this.$emit('change', val);
        }
    }
</script>
